import { makeStyles, shorthands, Spinner } from "@fluentui/react-components";
import { useNavigate } from "react-router-dom";
import { useIsGlobalAdmin } from "../apis/GraphApi";
import dtLogo from "../images/dt_logo.png";
import hdLogo from "../images/hd_logo.svg";
import laLogo from "../images/la_logo.png";
import lcLogo from "../images/lc_logo.png";
import spLogo from "../images/sp_logo.svg";
import tmLogo from "../images/tm_logo.png";
import toLogo from "../images/to_logo.png";
import { AppCard, DigitalTransformationAppCard, LicenseAssistantAppCard } from "./cards";
import { useGetFeatureFlags, useGetPermissions } from "../apis/CustomerManagementApi";
import { useState } from "react";
import { useQueryClient } from "react-query";
import { Auth } from "../utils/auth";
import config from "../config";
import { AdminGrantingDialog, ConfiguringAccessDialog } from "./dialogs";

const useStyles = makeStyles({
  box: {
    ...shorthands.padding("0px", "15px", "15px")
  },
  columnBox: {
    display: "flex",
    flexDirection: "column"
  },
  gridContainer: {
    display: "grid",
    columnGap: "15px",
    rowGap: "15px",
    gridTemplateColumns: "repeat(auto-fill, minmax(420px, 1fr))",
    "@media (max-width: 650px)": {
      gridTemplateColumns: "auto"
    }
  }
});

export const Services = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: isGlobalAdmin, isFetching } = useIsGlobalAdmin();
  const { data: featureFlags, isFetching: isFeatureFlagsFetching } = useGetFeatureFlags();
  const { data: permissions } = useGetPermissions(!!featureFlags?.tenantRegistered, error => {
      if (error.code === "InternalError") {
        console.error(error);
      } else {
        queryClient.setQueryData("notificationMessage", {
          intent: "error",
          title: "Error fetching app permissions",
          description: error.message
        })
      }
    });
  const [grantingDialogOpen, setGrantingDialogOpen] = useState(false);
  const [configuringDialogOpen, setConfiguringDialogOpen] = useState(false);
  const styles = useStyles();

  const onGrantingClick = async () => {
    const credential = Auth.getCredential();
    const user = await credential.getUserInfo();
    const redirectUri = `${config.infoboardBackendUrl}/tenants/registration`;
    const adminConsentUrl = `https://login.microsoftonline.com/${user.tenantId}/adminconsent?client_id=${config.backendClientId}&redirect_uri=${redirectUri}`;
    const size = 600;
    const top = (window.screen.height - size) / 2;
    const left = (window.screen.width - size) / 2;
    const newWindow = window.open(adminConsentUrl, "auth", `width=${size},height=${size},top=${top},left=${left}`);
    if (newWindow === null) {
      navigate("../tab");
      return;
    }
    
    const timer = setInterval(function () {
      if (newWindow.closed) {
        clearInterval(timer);
        setGrantingDialogOpen(false);
      }
      try {
        if (newWindow.location?.origin === window.location.origin) {
          clearInterval(timer);
          newWindow.close();
          setGrantingDialogOpen(false);
          setConfiguringDialogOpen(true);
        }
      } catch (e) {
        console.debug("Cannot read popup window location due to cross-origin policy");
      }
    }, 1000);
  }
  
  const onAdminGrantingCancelClick = () => {
    setGrantingDialogOpen(false);
    navigate("/tab");
  }

  const onConfiguringAccessCancelClick = () => {
    setConfiguringDialogOpen(false);
  }

  const onContinueClick = () => {
    window.open('https://admin.teams.microsoft.com/policies/app-setup')
  }

  if (isFetching || isFeatureFlagsFetching) {
    return <Spinner
      size="large"
      style={{
        flexGrow: 1
      }} 
    />;
  }

  // defined by value in DB and permission check if its completed
  const hasGraphPermissions = featureFlags?.tenantRegistered && (!permissions || permissions.permissionsGranted);

  return (
    <div className={styles.box}>
      <div className={styles.columnBox}>
        <h2>Admin apps</h2>
        <div className={styles.gridContainer}>
          <AppCard
            title="Tenant Optimizer"
            description="Boost your tenant's efficiency: Register, Analyze, Optimize!"
            img = {toLogo}
            allowed={isGlobalAdmin}
            onClick={() => navigate("../tenant-optimizer")} />
          <AppCard
            title="Tenant Pre-Migration Assistant"
            description="Explore your tenant-to-tenant migration project"
            img = {tmLogo}
            allowed={isGlobalAdmin}
            onClick={() => navigate("../migration-report")} />
          <AppCard
            title="Self-Service Portal"
            description="Your one-stop portal for Microsoft products management"
            img = {spLogo}
            href="https://portal.o365hq.com/#/customer/products"
            allowed={isGlobalAdmin} />
          <DigitalTransformationAppCard
            img={dtLogo}
            allowed={isGlobalAdmin} />
          {!featureFlags?.isHelpDeskEnabled && <AppCard
            title="HelpDesk"
            description="Get instant help with our integrated ticket management system"
            img = {hdLogo}
            onClick={() => navigate("../helpdesk/create-ticket")}
            allowed={isGlobalAdmin} />}
        </div>
        <h2>User apps</h2>
        <div className={styles.gridContainer}>
          <LicenseAssistantAppCard
            img = {laLogo}
            onClick={() => {
              if (hasGraphPermissions) {
                navigate("../license-order");
              } else {
                setGrantingDialogOpen(true);
              }
            }} />
          <AppCard
            title="License Comparison"
            description="Explore, Compare, Empower: Uncover Microsoft Product Insights with Ease!"
            img = {lcLogo}
            onClick={() => navigate("../license-comparison")} />
          {featureFlags?.isHelpDeskEnabled && <AppCard
            title="HelpDesk"
            description="Get instant help with our integrated ticket management system"
            img = {hdLogo}
            onClick={() => {
              if (hasGraphPermissions) {
                navigate("../helpdesk/create-ticket");
              } else {
                setGrantingDialogOpen(true);
              }
            }} />}
        </div>
      </div>
      <AdminGrantingDialog
        isOpen={grantingDialogOpen}
        onGrantingClick={onGrantingClick}
        onCancelClick={onAdminGrantingCancelClick}
      />
      <ConfiguringAccessDialog
        isOpen={configuringDialogOpen}
        onCancelClick={onConfiguringAccessCancelClick}
        onContinueClick={onContinueClick}
      />
    </div>
  );
}
